// ui/web/src/components/buzz/BuzzEmbeddedClient.tsx
import { useMemo, useEffect, useState, useRef } from "react"

// Buzz web client URL — configure via env
const BUZZ_WEB_CLIENT_URL = import.meta.env.VITE_BUZZ_WEB_URL ?? "https://buzz.company.com"
const BUZZ_WEB_ORIGIN = new URL(BUZZ_WEB_CLIENT_URL).origin

interface BuzzEmbeddedClientProps {
  relayUrl: string
  sessionToken: string
  mode: "sidebar" | "fullscreen"
  panelWidth: number
  onUnreadCountChange?: (count: number) => void
}

export function BuzzEmbeddedClient({
  relayUrl, sessionToken, mode, panelWidth, onUnreadCountChange,
}: BuzzEmbeddedClientProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [, setCurrentTheme] = useState<"dark" | "light">("dark")

  const buzzWebUrl = useMemo(() => {
    const url = new URL(BUZZ_WEB_CLIENT_URL)
    url.searchParams.set("relay",  relayUrl)
    url.searchParams.set("token",  sessionToken)
    url.searchParams.set("mode",   mode)
    url.searchParams.set("width",  String(panelWidth))
    return url.toString()
  }, [relayUrl, sessionToken, mode, panelWidth])

  // Listen for postMessage from Buzz iframe
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.origin !== BUZZ_WEB_ORIGIN) return    // Validate origin
      if (e.data?.type === "buzz:unread_count") {
        onUnreadCountChange?.(e.data.count as number)
      }
    }
    window.addEventListener("message", handler)
    return () => window.removeEventListener("message", handler)
  }, [onUnreadCountChange])

  return (
    <iframe
      ref={iframeRef}
      id="buzz-iframe"
      src={buzzWebUrl}
      className="w-full h-full border-0"
      title="Buzz chat"
      allow="microphone"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      aria-label="Buzz embedded chat"
    />
  )
}
